import { DELEGATION_MODULES, moduleDefinition, type ModuleKey, type ModuleState } from '@nest-recruit/shared';

// R1 (§L28, §L34): which switchable module each screen and palette command belongs to — one map,
// so the route gate (SYS-09), the Admin menu and the palette can never disagree about what a
// switched-off module hides. Anything not listed is core and always shown.

/** One part, or a list meaning any of them: the screen shows while one of them is on (Delegation, review R18). */
export type ModuleRequirement = ModuleKey | readonly ModuleKey[];

interface RouteRule {
  readonly pattern: RegExp;
  readonly module: ModuleRequirement;
}

/** Most specific first: the first rule that matches decides. */
const ROUTE_RULES: readonly RouteRule[] = [
  { pattern: /^\/organisation(\/|$)/, module: 'organisation' },
  { pattern: /^\/requisitions(\/|$)/, module: 'requisitions' },
  { pattern: /^\/request-hire(\/|$)/, module: 'requisitions' },
  { pattern: /^\/my\/openings(\/|$)/, module: 'requisitions' },
  // Starting a job FROM a requisition; `/jobs/new` itself (F5.24, a direct job) is core.
  { pattern: /^\/jobs\/new\/[^/]+$/, module: 'requisitions' },
  // REQ-04 lists hire-request approvals (Hire requests) and offer approvals (Offers); approvals run without Approval chains (ADR 0073),
  // and N-69 lands here (Admin redesign 2026-11-02, §L34).
  { pattern: /^\/approvals(\/|$)/, module: ['requisitions', 'offers'] },
  { pattern: /^\/referrals(\/|$)/, module: 'career_site' },
  { pattern: /^\/pools(\/|$)/, module: 'pools' },
  { pattern: /^\/cv-bank(\/|$)/, module: 'cv_bank' },
  { pattern: /^\/tasks(\/|$)/, module: 'tasks' },
  { pattern: /^\/assessments\/sessions(\/|$)/, module: 'exam_sessions' },
  { pattern: /^\/assessments(\/|$)/, module: 'assessments' },
  { pattern: /^\/offers\//, module: 'offers' },
  { pattern: /^\/workspace(\/|$)/, module: 'workspace' },
  { pattern: /^\/reports(\/|$)/, module: 'reports' },
  { pattern: /^\/jobs\/[^/]+\/auto-rejected$/, module: 'career_site' },
  { pattern: /^\/jobs\/[^/]+\/postings$/, module: 'career_site' },
  { pattern: /^\/jobs\/[^/]+\/assessment$/, module: 'assessments' },
  { pattern: /^\/jobs\/[^/]+\/compare$/, module: 'compare' },
  { pattern: /^\/jobs\/[^/]+\/offers$/, module: 'offers' },
  { pattern: /^\/jobs\/[^/]+\/pool$/, module: 'pools' },
  { pattern: /^\/jobs\/[^/]+\/setup\/form$/, module: 'career_site' },
  { pattern: /^\/jobs\/[^/]+\/setup\/assessment$/, module: 'assessments' },
  { pattern: /^\/jobs\/[^/]+\/setup\/offer$/, module: 'offers' },
  // Review R18 (§L34): cover moves hire-request and offer approvals, so it is there while either part is on.
  { pattern: /^\/admin\/delegation(\/|$)/, module: DELEGATION_MODULES },
  // Admin redesign 2026-11-02: hire-request chains belong to Approval chains, offer chains to Offers; the page shows while either is on.
  { pattern: /^\/admin\/approval-chains(\/|$)/, module: ['approvals', 'offers'] },
  // Time limits is its own page, gated only on its own part (never on Approval chains).
  { pattern: /^\/admin\/time-limits(\/|$)/, module: 'sla' },
  { pattern: /^\/admin\/offer-letters(\/|$)/, module: 'offers' },
  { pattern: /^\/admin\/pools-and-tags(\/|$)/, module: 'pools' },
  { pattern: /^\/admin\/inbox(\/|$)/, module: 'inbound_mail' },
  { pattern: /^\/admin\/role-templates(\/|$)/, module: 'templates' },
  { pattern: /^\/admin\/interview-plans(\/|$)/, module: 'templates' },
  { pattern: /^\/admin\/interview-kits(\/|$)/, module: 'interview_kits' },
  { pattern: /^\/admin\/career-site(\/|$)/, module: 'career_site' },
  { pattern: /^\/admin\/custom-fields(\/|$)/, module: 'custom_fields' },
  { pattern: /^\/admin\/fx-rates(\/|$)/, module: 'money' },
];

/** The switchable module a path belongs to (or the parts it needs any of), or null when it is core. */
export function moduleForPath(pathname: string): ModuleRequirement | null {
  return ROUTE_RULES.find((rule) => rule.pattern.test(pathname))?.module ?? null;
}

/** Whether a screen or command that needs this is on: its one part, or any of its parts. */
export function isRequirementOn(requirement: ModuleRequirement, modules: ModuleState): boolean {
  return typeof requirement === 'string' ? modules[requirement] : requirement.some((key) => modules[key]);
}

/** The part's name for "switched off", or every part's name when it needs any of several. */
export function requirementLabel(requirement: ModuleRequirement): string {
  const keys: readonly ModuleKey[] = typeof requirement === 'string' ? [requirement] : requirement;
  return keys.map((key) => moduleDefinition(key).label).join(' and ');
}

/** Palette commands registered by the features, by id (06-qol §1 keeps them in their own files). */
export const COMMAND_MODULES: Readonly<Record<string, ModuleRequirement>> = {
  'req.approvals': 'requisitions',
  'req.new': 'requisitions',
  'req.chains': ['approvals', 'offers'],
  'req.timeLimits': 'sla',
  'admin.delegation': DELEGATION_MODULES,
  'admin.fxRates': 'money',
  'admin.customFields': 'custom_fields',
  'admin.offerLetters': 'offers',
  'admin.inbox': 'inbound_mail',
  'jobs.templates': 'templates',
  'jobs.interview-plans': 'templates',
  'jobs.interview-kits': 'interview_kits',
  'jobs.career-site': 'career_site',
  'jobs.refer': 'career_site',
  'org.ledger': 'organisation',
  'org.headcount': 'organisation',
  'org.createPosition': 'organisation',
  'org.showArchived': 'organisation',
  'assessments.tests': 'assessments',
  'assessments.bank': 'assessments',
  'assessments.sessions': 'exam_sessions',
  'assessments.marking': 'assessments',
  'assessments.new-test': 'assessments',
  'assessments.new-question': 'assessments',
  'reports.workspace': 'workspace',
  'reports.commandCenter': 'reports',
  'reports.proficiency': 'reports',
  'reports.scoreboard': 'reports',
  'reports.dropOff': 'reports',
  'reports.builder': 'reports',
  'reports.metrics': 'reports',
  'pools.library': 'pools',
  'cvBank.open': 'cv_bank',
  'pools.admin': 'pools',
  'tasks.mine': 'tasks',
};
